/** Marketing i18n fragment — ucRemediation (use-cases/support-to-engineering-remediation). */

export const ucRemediationEn = {
  shot: {
    label: "Patch review",
    alt: "A proposed code change in the FixControl workspace with the diff, AI reasoning and verification, waiting for a human to approve or reject.",
  },
  meta: {
    title: "Turn support tickets into governed engineering fixes",
    description:
      "A support report from Freshdesk, Jira or FixControl becomes a governed mission: AI analyzes the issue against your codebase, proposes a patch as a reviewable diff, and runs tests and QA. Risky changes wait at approval gates, and delivery is a pull request.",
  },
  hero: {
    eyebrow: "Support → engineering",
    title: "Turn support tickets into",
    highlight: "governed engineering fixes",
    subtitle:
      "The expensive part of a bug is rarely typing the fix — it is the searching, the handovers and the waiting. A customer of your organization reports a problem; FixControl reads the ticket and the bound codebase, proposes a patch as a diff, and runs tests, QA and security checks. Risky changes pause at an approval gate, and delivery is a pull request your team reviews.",
    primaryCta: "Book a demo",
    secondaryCta: "Talk to us",
    bullet1: "Patch as reviewable diff",
    bullet2: "Risk gates for sensitive areas",
    bullet3: "Delivered as a pull request",
  },
  how: {
    eyebrow: "How it works",
    title: "From a ticket to a reviewed, gated patch",
    description: "Each step produces evidence for the next, and risky steps wait for human approval. The result: a shorter path from customer report to verified fix, with fewer handovers between support and engineering.",
  },
  steps: {
    report: {
      tag: "The report",
      title: "A customer of your organization, or someone on your support team, reports a problem",
      body: "A support conversation turns up what looks like a defect. It is captured in Freshdesk, Jira or as a FixControl issue.",
    },
    mission: {
      tag: "AI mission",
      title: "A mission analyzes the issue and the codebase",
      body: "FixControl opens a governed mission that reads the ticket alongside the bound codebase. It looks for the likely cause in the actual code paths involved.",
    },
    patch: {
      tag: "Proposed patch",
      title: "It proposes a patch — a diff to review",
      body: "The mission drafts a concrete change as a diff. Nothing is merged or shipped at this point; the diff exists for a person to inspect.",
    },
    checks: {
      tag: "Checks",
      title: "Tests, QA and security checks run",
      body: "The proposed change runs through tests, QA review and security checks. Their results are attached to the patch and travel with it into review.",
    },
    gates: {
      tag: "Approval gates",
      title: "Risky areas wait for a human gate",
      body: "Changes that touch sensitive areas — payments, data-loss paths, deployment — pause at an approval gate. Someone on your team decides, on the record, before the change moves forward.",
    },
  },
  features: {
    eyebrow: "What you get",
    title: "Traceable from customer report to approved change",
    description:
      "Every fix keeps its link back to the ticket that prompted it and the person who approved it.",
    ticket: {
      title: "Starts from a real ticket",
      body: "A remediation begins with an actual support report in Freshdesk, Jira or FixControl. Months later, you can still trace the code change back to your customer’s problem behind it.",
    },
    together: {
      title: "The issue and the codebase, read together",
      body: "The mission reasons over the report and the bound codebase to propose a targeted fix. It addresses the cause in code rather than the symptom in the ticket.",
    },
    diff: {
      title: "A diff you can review line by line",
      body: "The output is a concrete diff. Reviewers see exactly what would change before anything is accepted.",
    },
    evidence: {
      title: "Test, QA and security results attached",
      body: "Automated tests, QA review and security checks run against the proposal. The approver weighs the change with those results in hand.",
    },
    gates: {
      title: "Approval gates on risky changes",
      body: "Sensitive areas such as payments, data-loss paths and deployment are gated. Someone on your team approves, on the record, before the change proceeds.",
    },
    timeline: {
      title: "The whole trail, recorded",
      body: "An operational timeline records who did what and why, from report to mission to patch to approval to PR. An incident review or an audit can replay the sequence.",
    },
  },
  governed: {
    eyebrow: "The decision stays human",
    title: "Patches are proposals until a person accepts them",
    noteTitle: "How a patch gets accepted",
    noteBody:
      "A remediation mission proposes a patch as a reviewable diff and runs tests, QA and security checks before anyone accepts it. Changes touching payments, data-loss paths or deployment wait at an approval gate for a human decision. Delivery is a pull request or merge request, and the timeline records the whole path from report to approval.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions engineering leaders ask",
    q1: {
      q: "Does the AI merge or ship code by itself?",
      a: "No. The mission proposes a diff, checks run against it, and risky changes wait at a gate. Delivery is a pull request or merge request that your team reviews; nothing is pushed to a default branch.",
    },
    q2: {
      q: "Where does a remediation start?",
      a: "With a real support report: a Freshdesk ticket, a Jira issue or a FixControl issue. The engineering work stays traceable to that report.",
    },
    q3: {
      q: "What counts as a risky change?",
      a: "Changes touching areas such as payments, data-loss paths and deployment. These pause at an approval gate for a human decision before they advance.",
    },
    q4: {
      q: "What evidence does a reviewer see?",
      a: "The proposed diff, the results of tests, QA review and security checks, and the originating ticket. The approver judges the change in context.",
    },
    q5: {
      q: "Is there a record of what happened?",
      a: "Yes. The operational timeline and audit trail capture the sequence from report through mission, patch, checks, approval and PR.",
    },
    q6: {
      q: "Does FixControl deploy the fix to production?",
      a: "No. FixControl does not deploy to production on its own; delivery is a PR or merge request, and any promotion happens only after human approval at a deployment gate.",
    },
  },
  related: {
    approvalLedger: {
      label: "AI approval ledger",
      description: "The decision record behind every AI-assisted action.",
    },
    deployment: {
      label: "Deployment governance",
      description: "Gate promotions and see live rollout status.",
    },
    github: {
      label: "GitHub integration",
      description: "Patches arrive as pull requests with attached evidence.",
    },
  },
  cta: {
    title: "See a ticket become a fix",
    description:
      "Book a demo and follow one support report through analysis, patch, checks and approval.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const ucRemediationNl = {
  shot: {
    label: "Patch-review",
    alt: "Een voorgestelde codewijziging in de FixControl-workspace met de diff, AI-redenering en verificatie, wachtend op menselijke goedkeuring of afwijzing.",
  },
  meta: {
    title: "Verander supporttickets in gegoverneerde engineering-fixes",
    description:
      "Een supportmelding uit Freshdesk, Jira of FixControl wordt een gegoverneerde missie: AI analyseert het issue tegen je codebase, stelt een patch voor als een reviewbare diff, en draait tests en QA. Risicovolle wijzigingen wachten bij goedkeuringspoorten, en levering is een pull request.",
  },
  hero: {
    eyebrow: "Support → engineering",
    title: "Verander supporttickets in",
    highlight: "gegoverneerde engineering-fixes",
    subtitle:
      "Het dure deel van een bug is zelden het typen van de fix — het is het zoeken, de overdrachten en het wachten. Een klant van je organisatie meldt een probleem; FixControl leest het ticket en de gekoppelde codebase, stelt een patch als diff voor, en draait tests, QA en security-checks. Risicovolle wijzigingen pauzeren bij een goedkeuringspoort, en levering is een pull request die je team reviewt.",
    primaryCta: "Boek een demo",
    secondaryCta: "Neem contact op",
    bullet1: "Patch als reviewbare diff",
    bullet2: "Risicopoorten voor gevoelige gebieden",
    bullet3: "Geleverd als een pull request",
  },
  how: {
    eyebrow: "Hoe het werkt",
    title: "Van een ticket naar een gereviewde, gepoorte patch",
    description: "Elke stap levert bewijs op voor de volgende, en risicovolle stappen wachten op menselijke goedkeuring. Het resultaat: een kortere weg van klantmelding naar geverifieerde fix, met minder overdrachten tussen support en engineering.",
  },
  steps: {
    report: {
      tag: "De melding",
      title: "Een klant van je organisatie, of iemand van je supportteam, meldt een probleem",
      body: "Een supportgesprek brengt iets aan het licht dat op een defect lijkt. Het wordt vastgelegd in Freshdesk, Jira of als een FixControl-issue.",
    },
    mission: {
      tag: "AI-missie",
      title: "Een missie analyseert het issue en de codebase",
      body: "FixControl opent een gegoverneerde missie die het ticket samen met de gekoppelde codebase leest. Het zoekt de waarschijnlijke oorzaak in de code-paden die er daadwerkelijk bij betrokken zijn.",
    },
    patch: {
      tag: "Voorgestelde patch",
      title: "Het stelt een patch voor — een diff om te reviewen",
      body: "De missie stelt een concrete wijziging op als een diff. Er wordt op dit punt niets gemerged of uitgeleverd; de diff bestaat zodat een mens hem kan inspecteren.",
    },
    checks: {
      tag: "Checks",
      title: "Tests, QA en security-checks draaien",
      body: "De voorgestelde wijziging doorloopt tests, QA-review en security-checks. Hun resultaten worden aan de patch gehecht en reizen ermee mee de review in.",
    },
    gates: {
      tag: "Goedkeuringspoorten",
      title: "Risicovolle gebieden wachten op een menselijke poort",
      body: "Wijzigingen die gevoelige gebieden raken — betalingen, dataverliespaden, deployment — pauzeren bij een goedkeuringspoort. Iemand van je team beslist, vastgelegd, voordat de wijziging verdergaat.",
    },
  },
  features: {
    eyebrow: "Wat je krijgt",
    title: "Herleidbaar van klantmelding tot goedgekeurde wijziging",
    description:
      "Elke fix behoudt zijn link naar het ticket dat hem in gang zette en de persoon die hem goedkeurde.",
    ticket: {
      title: "Begint bij een echt ticket",
      body: "Een remediatie begint bij een echte supportmelding in Freshdesk, Jira of FixControl. Maanden later kun je de codewijziging nog steeds herleiden naar het probleem van je klant erachter.",
    },
    together: {
      title: "Het issue en de codebase, samen gelezen",
      body: "De missie redeneert over de melding en de gekoppelde codebase om een gerichte fix voor te stellen. Het pakt de oorzaak in de code aan in plaats van het symptoom in het ticket.",
    },
    diff: {
      title: "Een diff die je regel voor regel kunt reviewen",
      body: "De output is een concrete diff. Reviewers zien precies wat er zou veranderen voordat er iets wordt geaccepteerd.",
    },
    evidence: {
      title: "Tests-, QA- en security-resultaten bijgevoegd",
      body: "Geautomatiseerde tests, QA-review en security-checks draaien tegen het voorstel. De goedkeurder weegt de wijziging af met die resultaten in de hand.",
    },
    gates: {
      title: "Goedkeuringspoorten voor risicovolle wijzigingen",
      body: "Gevoelige gebieden zoals betalingen, dataverliespaden en deployment zijn gepoort. Iemand van je team keurt goed, vastgelegd, voordat de wijziging verdergaat.",
    },
    timeline: {
      title: "Het hele spoor, vastgelegd",
      body: "Een operationele tijdlijn legt vast wie wat deed en waarom, van melding naar missie naar patch naar goedkeuring naar PR. Een incidentreview of audit kan de reeks opnieuw afspelen.",
    },
  },
  governed: {
    eyebrow: "De beslissing blijft menselijk",
    title: "Patches zijn voorstellen totdat een mens ze accepteert",
    noteTitle: "Hoe een patch wordt geaccepteerd",
    noteBody:
      "Een remediatiemissie stelt een patch voor als een reviewbare diff en draait tests, QA en security-checks voordat iemand hem accepteert. Wijzigingen die betalingen, dataverliespaden of deployment raken, wachten bij een goedkeuringspoort op een menselijke beslissing. Levering is een pull request of merge request, en de tijdlijn legt het hele pad van melding tot goedkeuring vast.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen die engineering-leads stellen",
    q1: {
      q: "Merget of levert de AI code op eigen houtje uit?",
      a: "Nee. De missie stelt een diff voor, checks draaien ertegen, en risicovolle wijzigingen wachten bij een poort. Levering is een pull request of merge request die je team reviewt; er wordt niets naar een default branch gepusht.",
    },
    q2: {
      q: "Waar begint een remediatie?",
      a: "Bij een echte supportmelding: een Freshdesk-ticket, een Jira-issue of een FixControl-issue. Het engineering-werk blijft herleidbaar naar die melding.",
    },
    q3: {
      q: "Wat telt als een risicovolle wijziging?",
      a: "Wijzigingen die gebieden zoals betalingen, dataverliespaden en deployment raken. Deze pauzeren bij een goedkeuringspoort voor een menselijke beslissing voordat ze verdergaan.",
    },
    q4: {
      q: "Welk bewijs ziet een reviewer?",
      a: "De voorgestelde diff, de resultaten van tests, QA-review en security-checks, en het oorspronkelijke ticket. De goedkeurder beoordeelt de wijziging in context.",
    },
    q5: {
      q: "Is er een vastlegging van wat er gebeurde?",
      a: "Ja. De operationele tijdlijn en audittrail leggen de reeks vast, van melding via missie, patch, checks, goedkeuring en PR.",
    },
    q6: {
      q: "Deployt FixControl de fix naar productie?",
      a: "Nee. FixControl deployt niet zelf naar productie; levering is een pull request of merge request, en elke promotie gebeurt pas na menselijke goedkeuring bij een deployment-poort.",
    },
  },
  related: {
    approvalLedger: {
      label: "AI-goedkeuringsgrootboek",
      description: "Het beslissingsrecord achter elke AI-ondersteunde actie.",
    },
    deployment: {
      label: "Deployment-governance",
      description: "Poort promoties en zie live rollout-status.",
    },
    github: {
      label: "GitHub-integratie",
      description: "Patches verschijnen als pull requests met bijgevoegd bewijs.",
    },
  },
  cta: {
    title: "Zie een ticket een fix worden",
    description:
      "Boek een demo en volg één supportmelding door analyse, patch, checks en goedkeuring.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
